/**
 * Lightweight logger with level gating.
 *
 * In development: all levels are output (debug, info, warn, error).
 * In production:  only warn and error are output. Debug and info are
 *                 silenced to keep the console clean.
 *
 * Sentry's consoleLoggingIntegration captures log/warn/error — since we
 * use console.warn and console.error under the hood for those levels,
 * Sentry picks them up automatically. Debug and info use console.debug
 * and console.info respectively, which Sentry ignores by default.
 *
 * Usage:
 *   import { logger } from '../lib/logger'
 *   const log = logger('Sync')
 *   log.info('Starting sync for user', userId)   // [Sync] Starting sync for user ...
 *   log.error('Push failed:', error)              // [Sync] Push failed: ...
 *
 * You can also create an unscoped logger:
 *   import { log } from '../lib/logger'
 *   log.warn('Something happened')
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

const CONSOLE_FN: Record<LogLevel, (...args: unknown[]) => void> = {
  debug: console.debug,
  info: console.info,
  warn: console.warn,
  error: console.error,
}

function getMinLevel(): LogLevel {
  // Vite sets import.meta.env.MODE to 'development', 'production', or 'test'
  try {
    const mode = import.meta.env?.MODE
    if (mode === 'test') return 'warn'
    if (mode === 'production') return 'warn'
  } catch {
    // import.meta.env may not be available in all contexts
  }
  return 'debug'
}

const minLevel = getMinLevel()

export interface Logger {
  debug: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
}

function createLogger(scope?: string): Logger {
  const prefix = scope ? `[${scope}]` : ''

  function emit(level: LogLevel, args: unknown[]) {
    if (LEVEL_ORDER[level] < LEVEL_ORDER[minLevel]) return
    const fn = CONSOLE_FN[level]
    if (prefix && args.length > 0 && typeof args[0] === 'string') {
      // Prepend prefix into the first string arg so %s/%d format specifiers work
      fn(`${prefix} ${args[0]}`, ...args.slice(1))
    } else if (prefix) {
      fn(prefix, ...args)
    } else {
      fn(...args)
    }
  }

  return {
    debug: (...args: unknown[]) => emit('debug', args),
    info: (...args: unknown[]) => emit('info', args),
    warn: (...args: unknown[]) => emit('warn', args),
    error: (...args: unknown[]) => emit('error', args),
  }
}

/**
 * Create a scoped logger. The scope is prefixed to every message as `[Scope]`.
 *
 * @example
 *   const log = logger('Sync')
 *   log.info('Starting sync')  // [Sync] Starting sync
 */
export function logger(scope: string): Logger {
  return createLogger(scope)
}

/** Unscoped logger for one-off messages. */
export const log: Logger = createLogger()
